<template>
  <div class="space-y-24" data-test="rule-form-fields">
    <div class="border-static-default-mid rounded-md space-y-24 border p-20">
      <span class="body-2-bold">
        {{ i18n(translations.availableMarkets) }}
      </span>

      <div
        class="gap-6"
        :class="{ 'flex flex-wrap': !hasSubMarkets }"
        data-test="markets"
      >
        <div
          v-for="marketWithSubMarkets in availableMarkets"
          :key="marketWithSubMarkets.market"
        >
          <ToggleMarket
            :key="toggleMarketKey"
            :category-group-metadata
            :current-methods
            :current-selection="selectedMarketsWithSubMarkets"
            :is-free
            :market-with-sub-market="marketWithSubMarkets"
            :other-methods
            :should-show-collection-point-options
            @selected="handleMarketSelected"
          />
        </div>
      </div>

      <div
        v-if="selectedMarketsWithSubMarkets.length"
        class="divide-static-default-mid divide-y md:space-y-16 md:divide-y-0"
      >
        <div
          v-for="currency in currencies"
          :key="currency"
          class="mt-16 pt-16 first:mt-0 first:pt-0 md:mt-0 md:pt-0"
        >
          <AddMethod
            :category-group-metadata
            :currency
            :current-methods
            :is-free
            :is-free-mandatory
            :markets="marketsByCurrency[currency] || []"
            :other-methods
            :should-show-collection-point-options
            :validation-thresholds
            @method-add="handleMethodAdd"
          />
        </div>
      </div>
    </div>

    <RevInfoBlock
      v-if="shouldDisplayTimeError"
      :icon="IconWarning"
      :title="i18n(translations.errorPaidMethodTimeNotFastEnough)"
      variant="danger"
    />

    <MethodList
      :category-group-metadata
      :current-methods
      :is-free
      :is-free-mandatory
      :other-methods
      :should-show-collection-point-options
      @delete-method="handleMethodDeleted"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import type { Currency, MarketCountryCode } from '@backmarket/http-api'
import type { MarketWithSubMarkets } from '@backmarket/http-api/src/api-specs-shipping/rules'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { getMarketplace } from '@backmarket/nuxt-module-marketplace/getMarketplace'
import { isEqual } from '@backmarket/utils/object/isEqual'
import { RevInfoBlock } from '@ds/components/InfoBlock'
import { IconWarning } from '@ds/icons/IconWarning'

import { NO_CURRENCY } from '../../../../CategoryList.constants'
import type {
  PotentialCurrency,
  ShippingCategoryGroupMetadata,
} from '../../../../CategoryList.types'
import ToggleMarket from '../../../ToggleMarket/ToggleMarket.vue'
import type { FormMethodValues } from '../../RuleDrawer.types'
import {
  getMinShippingTime,
  isPaidMethodTimeFastEnough,
} from '../../RuleDrawer.utils'
import { useHttpGetThresholds } from '../../composables/useHttpGetThresholds'

import translations from './RuleFormFields.translations'
import AddMethod from './components/AddMethod/AddMethod.vue'
import MethodList from './components/MethodList/MethodList.vue'

// This is a trick to force the re-render of the ToggleMarket component
// when adding or removing a method (to deselect market and submarkets)
const toggleMarketKey = ref(`toggle-markets-${crypto.randomUUID()}`)

const props = withDefaults(
  defineProps<{
    currentMethods: FormMethodValues[]
    otherMethods: FormMethodValues[]
    availableMarkets: MarketWithSubMarkets[]
    isFree?: boolean
    isFreeMandatory?: boolean
    categoryGroupMetadata: ShippingCategoryGroupMetadata
    shouldShowCollectionPointOptions?: boolean
  }>(),
  {
    isFree: false,
    isFreeMandatory: true,
    shouldShowCollectionPointOptions: false,
  },
)

const i18n = useI18n()
const selectedMarketsWithSubMarkets = ref<MarketWithSubMarkets[]>([])

// Needed for the API call, passing directly the ref breaks the reactivity
const groupId = computed(() => props.categoryGroupMetadata.id)
const selectedMarkets = computed(() =>
  selectedMarketsWithSubMarkets.value?.map(
    ({ market }) => market as MarketCountryCode,
  ),
)

const { data: validationThresholds } = useHttpGetThresholds(
  groupId,
  selectedMarkets,
  props.isFree
    ? ['TRANSIT_TIME_THRESHOLDS']
    : ['PRICE_THRESHOLDS', 'TRANSIT_TIME_THRESHOLDS'],
)

const marketsByCurrency = computed(() => {
  const currenciesObj: Partial<
    Record<PotentialCurrency, MarketWithSubMarkets[]>
  > = {}
  if (props.isFree) {
    currenciesObj[NO_CURRENCY] = selectedMarketsWithSubMarkets.value
  } else {
    selectedMarketsWithSubMarkets.value.forEach((marketWithSubMarkets) => {
      const currency = getMarketplace(marketWithSubMarkets.market).market
        .defaultCurrency
      if (!currenciesObj[currency]) {
        currenciesObj[currency] = []
      }
      // Cast only to prevent TS error "Object is possibly 'undefined'"
      ;(currenciesObj[currency] as MarketWithSubMarkets[]).push(
        marketWithSubMarkets,
      )
    })
  }

  return currenciesObj
})

const hasSubMarkets = computed(() =>
  props.availableMarkets.some(
    (marketWithSubMarkets) => marketWithSubMarkets.subMarkets.length > 0,
  ),
)

const currencies = computed<PotentialCurrency[]>(() =>
  props.isFree
    ? [NO_CURRENCY]
    : (Object.keys(marketsByCurrency.value).sort() as Currency[]),
)

const minFreeShippingTime = computed(() => {
  return getMinShippingTime(
    props.otherMethods,
    props.currentMethods
      .map(({ markets }) =>
        markets
          .map(({ market, subMarkets }) =>
            subMarkets.length > 0
              ? subMarkets.map(({ subMarketCode }) => subMarketCode)
              : market,
          )
          .flat(),
      )
      .flat(),
  )
})

const shouldDisplayTimeError = computed(() => {
  if (props.isFree || !props.isFreeMandatory) {
    return false
  }

  return (
    props.currentMethods.length &&
    props.currentMethods.every(
      ({ shippingTime }) =>
        !isPaidMethodTimeFastEnough(+shippingTime, minFreeShippingTime.value),
    )
  )
})

function handleMethodAdd() {
  toggleMarketKey.value = `toggle-markets-${crypto.randomUUID()}`
  selectedMarketsWithSubMarkets.value = []
}

function handleMethodDeleted() {
  toggleMarketKey.value = `toggle-markets-${crypto.randomUUID()}`
}

function removeMarketWithSubMarketFromSelection(
  marketWithSubMarket: MarketWithSubMarkets,
) {
  selectedMarketsWithSubMarkets.value =
    selectedMarketsWithSubMarkets.value.filter(
      (market) => market.market !== marketWithSubMarket.market,
    )
}

function addMarketWithSubMarketToSelection(
  marketWithSubMarket: MarketWithSubMarkets,
) {
  selectedMarketsWithSubMarkets.value.push(marketWithSubMarket)
}

function mergeMarketWithSubMarket(marketWithSubMarket: MarketWithSubMarkets) {
  removeMarketWithSubMarketFromSelection(marketWithSubMarket)
  if (marketWithSubMarket.subMarkets.length > 0) {
    addMarketWithSubMarketToSelection(marketWithSubMarket)
  }
}

function handleMarketSelected(marketWithSubMarket: MarketWithSubMarkets) {
  const isMarketAlreadySelected = selectedMarketsWithSubMarkets.value.find(
    (market) => market.market === marketWithSubMarket.market,
  )
  const initialMarketWithSubMarketsDescription = props.availableMarkets.find(
    ({ market }) => market === marketWithSubMarket.market,
  )

  const initialMarketHasSubMarkets =
    initialMarketWithSubMarketsDescription?.subMarkets
      ? initialMarketWithSubMarketsDescription?.subMarkets.length > 0
      : false

  if (isMarketAlreadySelected) {
    const hasSameSubMarkets = isEqual(
      isMarketAlreadySelected.subMarkets,
      marketWithSubMarket.subMarkets,
    )

    if (hasSameSubMarkets) {
      removeMarketWithSubMarketFromSelection(marketWithSubMarket)
    } else {
      mergeMarketWithSubMarket(marketWithSubMarket)
    }
  } else if (
    marketWithSubMarket?.subMarkets?.length > 0 ||
    !initialMarketHasSubMarkets
  ) {
    addMarketWithSubMarketToSelection(marketWithSubMarket)
  } else {
    removeMarketWithSubMarketFromSelection(marketWithSubMarket)
  }
}
</script>
